<template>
  <div v-if="isLoaded" class="relative">
    <MagnifyingGlassIcon
      class="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-500"
      aria-hidden="true"
    />
    <vue3-simple-typeahead
      id="typeahead_id"
      class="block h-full w-full border-0 bg-transparent py-0 pl-8 pr-0 text-gray-500 focus:ring-0 sm:text-sm"
      placeholder="Sök..."
      :items="computedSearchResult"
      :minInputLength="3"
      :itemProjection="itemProjectionFunction"
      @onInput="onInputEventHandler"
      @onBlur="onBlurEventHandler"
      @selectItem="selectItemEventHandler"
      ref="inputRef"
    >
      <!-- <template #list-header> Resultat {{ items.length }} </template> -->
      <template #list-item-text="slot">
        <a
          v-if="slot.item?.identifier === 'applicantpositionresponse'"
          class="grid grid-cols-[auto,1fr] gap-4 py-2 group"
        >
          <div
            :class="[
              'bg-sea-1 mb-0 self-center row-span-full rounded-full mx-auto overflow-hidden w-10 h-10 text-center items-center justify-center flex',
            ]"
          >
            <i
              class="fas fa-user text-lg text-white"
              v-if="!slot.item?.data?.avatar"
            ></i>
            <img v-else :src="slot.item?.data?.avatar" />
          </div>
          <div class="text-darkGray-1 sm:pr-8">
            <h5 class="font-semibold text-gray-900">
              {{ slot.item?.data?.info?.firstname }}
              {{ slot.item?.data?.info?.lastname }}
              <span
                class="px-2 bg-gray-3 group-hover:bg-white rounded-md text-base font-normal"
              >
                #{{ slot.item?.data?.id }}
              </span>
            </h5>

            <p class="hidden text-sm sm:block">
              <i class="fas fa-envelope mr-2 text-azure-1"></i
              >{{ slot.item?.data?.user?.email }}
            </p>
            <p class="hidden text-sm sm:block">
              <i class="text-blue-1 fa-solid fa-location-crosshairs mr-2"></i
              >{{ slot.item?.data?.position?.name }}
            </p>
          </div>
        </a>
        <a v-else class="grid grid-cols-[auto,1fr] gap-4 py-2 group">
          <div
            :class="[
              'bg-purple-1 mb-0 self-center row-span-full rounded-full mx-auto overflow-hidden w-10 h-10 text-center items-center justify-center flex',
            ]"
          >
            <i class="fa-solid fa-location-crosshairs text-lg text-white"></i>
          </div>
          <div class="text-darkGray-2 sm:pr-8 flex flex-col gap-1">
            <h5 class="font-semibold text-gray-900">
              {{ slot.item?.data?.name }}
              <span
                class="px-2 bg-gray-3 group-hover:bg-white rounded-md text-base font-normal"
              >
                #{{ slot.item?.data?.id }}
              </span>
            </h5>
            <div class="flex gap-2">
              <span
                :class="[
                  'text-white uppercase py-1 px-2 rounded-lg text-sm w-fit h-fit',
                  isUnixDateBeforeToday(slot.item?.data?.stop) ||
                  isUnixDateAfterToday(slot.item?.data?.start)
                    ? 'bg-red-2'
                    : 'bg-blue-1',
                ]"
              >
                {{
                  isUnixDateBeforeToday(slot.item?.data?.stop) ||
                  isUnixDateAfterToday(slot.item?.data?.start)
                    ? "Stängd"
                    : "Öppen"
                }}
              </span>
              <div
                :title="`${slot.item?.data?.totalApplicants} person(er) har ansökt`"
                :class="[
                  'bg-gray-3 group-hover:bg-white flex row-wrap gap-2 py-1 px-2 items-center rounded-lg text-xs w-fit h-fit',
                  slot.item?.data?.totalApplicants > 0
                    ? 'text-purple-1 '
                    : 'text-darkGray-4 ',
                ]"
              >
                <i class="fas fa-users"></i>
                <p class="text-sm">{{ slot.item?.data?.totalApplicants }}</p>
              </div>
              <div
                :title="`${slot.item?.data?.totalUnProcessed} ansökning(ar) behöver hanteras`"
                :class="[
                  'bg-gray-3 group-hover:bg-white flex row-wrap gap-2 py-1 px-2 items-center rounded-lg text-xs w-fit h-fit',
                  slot.item?.data?.totalUnProcessed > 0
                    ? 'text-azure-1'
                    : 'text-darkGray-4',
                ]"
              >
                <i class="fas fa-hourglass"></i>
                <p class="text-sm">{{ slot.item?.data?.totalUnProcessed }}</p>
              </div>
            </div>
          </div>
        </a>
      </template>
      <!-- <template #list-footer> LIST FOOTER </template> -->
    </vue3-simple-typeahead>
    <div v-if="searchPending" class="absolute right-2 -top-1">
      <i class="fas fa-circle-notch fa-spin text-blue-1 group-hover:hidden"></i>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import store from "@/store";
import router from "@/router";
import moment from "moment";
import { MagnifyingGlassIcon } from "@heroicons/vue/20/solid";

const inputRef = ref(null);

const isLoaded = ref(false);
const searchPending = ref(false);

onMounted(async () => {
  isLoaded.value = true;
});

function selectItemEventHandler(e: any) {
  if (e.identifier === "applicantpositionresponse") {
    const url =
      "/admin/positions/" + e.data.positionId + "/applicants/" + e.data.id;
    router.push(url);
  } else if (e.identifier === "positionresponse") {
    const url = "/admin/positions/" + e.data.id;
    router.push(url);
  }
}

const today = moment();
const todayInUnix = today.unix();

const isUnixDateBeforeToday = (unixDate: number) => {
  return unixDate < todayInUnix;
};

const isUnixDateAfterToday = (unixDate: number) => {
  return unixDate > todayInUnix;
};

async function onInputEventHandler(e: any) {
  if ((e.input as string).length > 2) {
    searchPending.value = true;
    await store.dispatch("search", e.input);
    searchPending.value = false;
  }
}

const computedSearchResult = computed(() => {
  // returns unique search result based on id
  const uniqueItems = new Map();
  store.getters.searchResult.forEach((item: any) => {
    uniqueItems.set(item.data.id, item);
  });
  return Array.from(uniqueItems.values());
});

function itemProjectionFunction(item: any) {
  if (item.identifier === "applicantpositionresponse") {
    return [
      item?.data?.user?.email,
      item?.data?.info?.firstname,
      item?.data?.info?.lastname,
      item?.data?.info?.ssn,
      item?.data?.info?.city,
      item?.data?.info?.zipCode,
      item?.data?.id?.toString(),
    ].join(" ");
  } else {
    return item?.data?.name + " " + item?.data?.id;
  }
}
function onBlurEventHandler() {
  inputRef.value.clearInput();
}
</script>
<style>
input.simple-typeahead-input {
  position: relative;
}
div.simple-typeahead-list {
  @apply absolute;
  @apply top-8;
  @apply shadow-sm;
  @apply z-50;
  @apply w-full;
  @apply bg-white;
  @apply border;
  @apply border-gray-100;
  @apply rounded-lg;
}

.simple-typeahead-list-item {
  @apply border-b;
  @apply border-gray-100;
  @apply hover:bg-gray-100;
  @apply cursor-pointer;
  @apply px-4;
}
</style>
